import { CheckOutlined, SyncOutlined } from "@ant-design/icons";
import {
  Card,
  Row,
  Col,
  Typography,
  Radio,
  Space,
  Input,
  Button,
  Spin,
  Select,
  notification,
} from "antd";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useConfirmPinCodeMutation,
  useSendPinCodeMutation,
  useSignRutokenMutation,
} from "../../store/services/eds-service";

import {
  listRutokenCerts,
  listRutokenDevices,
  loadRutokenPlugin,
  loginRutoken,
  logoutRutoken,
  rutokenErrorText,
  rutokenFailText,
} from "../../features/auth/rutokenPlugin";
import { useLazyGetReportByIdQuery } from "../../store/services/report-service";
import TokenSignForm from "../../components/eds/TokenSignForm";

const { Title, Text } = Typography;
const Eds = () => {
  const navigate = useNavigate();
  // report id
  const { reportId } = useParams();
  const [getReportById] = useLazyGetReportByIdQuery();
  // Eds
  const [eds, setEds] = useState();
  const [pin, setPin] = useState("");

  // RuToken
  const [plugin, setPlugin] = useState();
  const [currentRutoken, setCurrentRutoken] = useState({});
  const [deviceList, setDeviceList] = useState([]);
  const [currentCert, setCurrentCert] = useState({});
  const [certList, setCertList] = useState([]);
  const [signRutoken, { isSuccess: isSuccessSignRutoken }] =
    useSignRutokenMutation();
  const [isSigningRutoken, setIsSigningRutoken] = useState(false);

  // Eds handlers
  const [
    sendPinCode,
    { isSuccess: isSuccessSendPin, isLoading: isLoadingSendPin },
  ] = useSendPinCodeMutation();

  const [
    confirmEdsPinCode,
    { isLoading: isLoadingConfirmPin, isSuccess: isSuccessConfirmPin },
  ] = useConfirmPinCodeMutation();

  const confirmPinCodeHandler = () => {
    if (pin.trim() == "") return false;
    if (eds === 1) {
      confirmEdsPinCode({ pin, reportId });
    } else {
      confirmRotokenPinCide(pin);
    }
  };
  const onSetEds = (e) => {
    setEds(e.target.value);
  };

  const checkDevices = async (instance = plugin) => {
    if (!instance) return;
    try {
      const devices = await listRutokenDevices(instance);
      if (devices.length > 0) {
        const list = devices.map((device) => ({
          value: device,
          label: `Рутокен ЭЦП #${device}`,
        }));
        setDeviceList(list);
        setCurrentRutoken(list[0]);
        await checkCerts(instance, list[0].value);
      } else {
        setDeviceList([]);
        setCurrentRutoken({});
        const text = rutokenFailText(new Error("DEVICE_MISSING"));
        notification.error({
          message: text.message,
          description: text.description,
        });
      }
    } catch (error) {
      const text = rutokenFailText(error);
      notification.error({
        message: text.message,
        description: text.description,
      });
    }
  };
  const checkCerts = async (instance = plugin, device = currentRutoken.value) => {
    if (!instance || device === undefined) return;
    try {
      const list = await listRutokenCerts(instance, device);
      setCertList(list);
      setCurrentCert(list[0] || {});
      if (!list.length) {
        notification.error({ message: "Сертификат на Рутокен не обнаружен" });
      }
    } catch (error) {
      notification.error({ message: rutokenErrorText(instance, error) });
    }
  };
  const confirmRotokenPinCide = async (pin) => {
    if (!plugin || currentRutoken.value === undefined || !currentCert.value) {
      notification.error({ message: "Выберите Рутокен и сертификат" });
      return;
    }
    const device = currentRutoken.value;
    setIsSigningRutoken(true);
    try {
      const { data } = await getReportById(reportId);
      if (!data) throw new Error("Не удалось загрузить отчёт");
      await loginRutoken(plugin, device, pin);
      const hash = await plugin.sign(
        device,
        currentCert.value,
        JSON.stringify(data.content),
        plugin.DATA_FORMAT_PLAIN,
        {}
      );
      await signRutoken({ reportId, hash, cert: currentCert.data }).unwrap();
    } catch (error) {
      const serverMessage = error?.data?.message;
      notification.error({
        message: serverMessage
          ? String(serverMessage)
          : rutokenErrorText(plugin, error),
      });
    } finally {
      await logoutRutoken(plugin, device);
      setIsSigningRutoken(false);
    }
  };

  useEffect(() => {
    if (eds && eds === 1 && !isSuccessSendPin) {
      sendPinCode();
    }
    if (eds && eds === 2 && !isSuccessSignRutoken) {
      let cancelled = false;
      (async () => {
        try {
          const instance = await loadRutokenPlugin();
          if (cancelled) return;
          setPlugin(instance);
          const devices = await listRutokenDevices(instance);
          if (cancelled) return;
          if (devices.length > 0) {
            const list = devices.map((device) => ({
              value: device,
              label: `Рутокен ЭЦП #${device}`,
            }));
            setDeviceList(list);
            setCurrentRutoken(list[0]);
            await checkCerts(instance, list[0].value);
          } else {
            const text = rutokenFailText(new Error("DEVICE_MISSING"));
            notification.error({
              message: text.message,
              description: text.description,
            });
          }
        } catch (error) {
          if (cancelled) return;
          const text = rutokenFailText(error);
          notification.error({
            message: text.message,
            description: text.description,
          });
        }
      })();
      return () => {
        cancelled = true;
      };
    }
    if (
      (eds === 1 && isSuccessConfirmPin) ||
      (eds === 2 && isSuccessSignRutoken)
    ) {
      navigate("/dashboard/reports");
    }
  }, [eds, isSuccessConfirmPin, isSuccessSignRutoken]);
  return (
    <Row gutter={16}>
      <Col span={24}>
        <Card
          bordered={false}
          className="criclebox mb-24"
          title={<Title level={4}>Выберите тип электронной подписи</Title>}
        >
          <Radio.Group onChange={onSetEds} value={eds}>
            <Space direction="vertical">
              <Radio value={1}>Облачное ЭЦП</Radio>
              <Radio value={2}>Рутокен ЭЦП</Radio>
              <Radio value={3}>JaCarta ЭЦП</Radio>
              <Radio value={4}>EnoToken ЭЦП</Radio>
            </Space>
          </Radio.Group>
        </Card>
      </Col>

      {eds && eds == 1 && isSuccessSendPin && (
        <Col span={24}>
          <Card
            bordered={false}
            className="criclebox mb-24"
            title={<Title level={4}>Облачное ЭЦП</Title>}
          >
            <Row gutter={8}>
              <Col span={6}>
                <Input
                  style={{
                    width: "100%",
                  }}
                  placeholder="Введите пин код"
                  onChange={(e) => setPin(e.target.value)}
                />
              </Col>
              <Col span={6}>
                <Button
                  loading={isLoadingConfirmPin}
                  type="primary"
                  style={{
                    background: "#57b6c0",
                    borderColor: "#57b6c0",
                  }}
                  onClick={confirmPinCodeHandler}
                >
                  Подтвердить
                </Button>
              </Col>
            </Row>
          </Card>
        </Col>
      )}
      {eds && eds == 2 && plugin && (
        <Col span={24}>
          <Card
            bordered={false}
            className="criclebox mb-24"
            title={<Title level={4}>Рутокен</Title>}
          >
            <Row gutter={8} style={{ marginBottom: "16px" }}>
              <Col span={8}>
                <Select
                  placeholder="Выберете устройства"
                  value={currentRutoken.value ?? null}
                  style={{
                    width: "100%",
                  }}
                  options={deviceList}
                  onChange={(value, option) => {
                    setCurrentRutoken(option);
                    setCertList([]);
                    setCurrentCert({});
                    checkCerts(plugin, value);
                  }}
                />
              </Col>
              <Col span={6}>
                <Button
                  type="primary"
                  style={{
                    width: "100%",
                    background: "#57b6c0",
                    borderColor: "#57b6c0",
                  }}
                  onClick={() => checkDevices()}
                  icon={<SyncOutlined />}
                >
                  Обновить список рутокенов
                </Button>
              </Col>
            </Row>
            {Object.keys(currentRutoken).length != 0 && (
              <Row gutter={8} style={{ marginBottom: "16px" }}>
                <Col span={8}>
                  <Select
                    value={currentCert.value ?? null}
                    style={{
                      width: "100%",
                    }}
                    options={certList}
                    onChange={(value, option) => setCurrentCert(option)}
                  />
                </Col>
                <Col span={6}>
                  <Button
                    icon={<SyncOutlined />}
                    type="primary"
                    style={{
                      width: "100%",
                      background: "#57b6c0",
                      borderColor: "#57b6c0",
                    }}
                    onClick={() => checkCerts()}
                  >
                    Обновить список сертификатов
                  </Button>
                </Col>
              </Row>
            )}

            {Object.keys(currentCert).length != 0 && (
              <Row gutter={8}>
                <Col span={8}>
                  <Input.Password
                    style={{
                      width: "100%",
                    }}
                    placeholder="Введите пин код"
                    onChange={(e) => setPin(e.target.value)}
                    onPressEnter={confirmPinCodeHandler}
                  />
                </Col>
                <Col span={6}>
                  <Button
                    type="primary"
                    loading={isSigningRutoken}
                    onClick={confirmPinCodeHandler}
                    style={{
                      width: "100%",
                      background: "#57b6c0",
                      borderColor: "#57b6c0",
                    }}
                    icon={<CheckOutlined />}
                  >
                    Подтвердить пин код
                  </Button>
                </Col>
              </Row>
            )}
          </Card>
        </Col>
      )}
      {eds === 3 && (
        <Col span={24}>
          <Card
            bordered={false}
            className="criclebox mb-24"
            title={<Title level={4}>JaCarta</Title>}
          >
            <TokenSignForm
              key="jacarta"
              kind="jacarta"
              reportId={reportId}
              onSuccess={() => navigate("/dashboard/reports")}
            />
          </Card>
        </Col>
      )}
      {eds === 4 && (
        <Col span={24}>
          <Card
            bordered={false}
            className="criclebox mb-24"
            title={<Title level={4}>EnoToken</Title>}
          >
            <TokenSignForm
              key="enotoken"
              kind="enotoken"
              reportId={reportId}
              onSuccess={() => navigate("/dashboard/reports")}
            />
          </Card>
        </Col>
      )}
    </Row>
  );
};
export default Eds;
